import React, { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { MagnifyingGlass, Check, Plus, CaretDown, X } from '@phosphor-icons/react'
import { cn } from '@/lib/utils'

export interface ContactListOption {
  id: number
  name: string
}

/**
 * What the combobox currently resolves to:
 *  - none:     nothing chosen
 *  - existing: an existing list was picked
 *  - new:      a name was typed that doesn't match any list (create-on-save)
 */
export type ContactListSelection =
  | { mode: 'none' }
  | { mode: 'existing'; id: number; name: string }
  | { mode: 'new'; name: string }

export const NO_LIST: ContactListSelection = { mode: 'none' }

export function selectionName(v: ContactListSelection): string {
  return v.mode === 'none' ? '' : v.name
}

export interface ContactListComboboxProps {
  lists: ContactListOption[]
  value: ContactListSelection
  onChange: (value: ContactListSelection) => void
  /** Allow typing a new name that becomes a new list. Default true. */
  allowCreate?: boolean
  /** Allow clearing back to "no list" (empty). Default false. */
  allowClear?: boolean
  placeholder?: string
  ariaLabel?: string
  autoFocus?: boolean
  /** Stronger visual treatment (e.g. featured in the import modal). */
  emphasized?: boolean
  disabled?: boolean
  className?: string
}

const MENU_MAX = 288
// Smallest usable menu height (~3 short rows). The menu is portal-rendered and
// floats over everything, so we only flip it above the input when there is
// genuinely not enough room below for even this — otherwise it always opens down.
const MENU_MIN = 96

/**
 * Suggestive-search picker for contact lists. Type to filter existing lists;
 * an unmatched name surfaces a "Create" row (when allowCreate) so saving makes
 * a new list. The menu is portal-rendered with viewport-clamped height so it
 * never runs off-screen. Pure UI — the caller decides what to do with the
 * resolved selection.
 */
export const ContactListCombobox: React.FC<ContactListComboboxProps> = ({
  lists,
  value,
  onChange,
  allowCreate = true,
  allowClear = false,
  placeholder = 'Search or create a list…',
  ariaLabel = 'Contact list',
  autoFocus = false,
  emphasized = false,
  disabled = false,
  className,
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState(selectionName(value))
  const [activeIndex, setActiveIndex] = useState(0)
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null)

  const inputRef = useRef<HTMLInputElement | null>(null)
  const wrapRef = useRef<HTMLDivElement | null>(null)
  const menuRef = useRef<HTMLUListElement | null>(null)

  // Stable id base so the input can point `aria-activedescendant` at the
  // highlighted row — mirrors Select.tsx (§57). `optionId` shares the same
  // index space as `activeIndex`: filtered rows use their array index, and
  // the "create new list" row (when shown) occupies `filtered.length`.
  const uid = React.useId()
  const listboxId = `${uid}listbox`
  const optionId = useCallback((index: number) => `${uid}opt-${index}`, [uid])

  // Mirror external selection changes into the visible text while not editing.
  useEffect(() => {
    if (!open) setQuery(selectionName(value))
  }, [value, open])

  const q = query.trim()
  const ql = q.toLowerCase()
  const filtered = q ? lists.filter((l) => l.name.toLowerCase().includes(ql)) : lists
  const exactMatch = lists.find((l) => l.name.trim().toLowerCase() === ql)
  const showCreate = allowCreate && q.length > 0 && !exactMatch
  const rowCount = filtered.length + (showCreate ? 1 : 0)

  const computeMenuStyle = useCallback((): React.CSSProperties | null => {
    const rect = wrapRef.current?.getBoundingClientRect()
    if (!rect) return null
    const margin = 6
    const spaceBelow = window.innerHeight - rect.bottom - margin
    const spaceAbove = rect.top - margin
    // Prefer opening downward. Only flip up when there isn't even a minimal
    // menu's worth of room below and there's clearly more space above.
    const openUp = spaceBelow < MENU_MIN && spaceAbove > spaceBelow
    const available = Math.max(0, openUp ? spaceAbove : spaceBelow)
    const maxHeight = Math.min(MENU_MAX, available)
    // Anchor the flush edge to the trigger. Opening up, pin the menu's BOTTOM
    // just above the input and grow upward — anchoring via `top` (rect.top -
    // maxHeight) floats the menu high with a gap when it's shorter than maxHeight.
    const base: React.CSSProperties = { position: 'fixed', left: rect.left, width: rect.width, maxHeight, zIndex: 1000 }
    return openUp
      ? { ...base, bottom: window.innerHeight - rect.top + margin }
      : { ...base, top: rect.bottom + margin }
  }, [])

  const close = useCallback(() => {
    setOpen(false)
    setActiveIndex(0)
  }, [])

  // Position the menu once when it opens, then DISMISS on scroll or resize
  // (§54 R2). The menu is fixed to the viewport and never chases its trigger,
  // so letting the page scroll under it would leave a menu anchored to
  // nothing — repositioning-on-scroll is rejected too, since it costs a
  // layout read per scroll frame and still leaves the menu attached to a
  // trigger the user has scrolled away from on purpose. Otherwise it closes
  // on outside-click / Escape / selection. Mirrors Select.tsx.
  //
  // The scroll listener is capture-phase because `scroll` does not bubble —
  // that is what catches scrolling inside nested ancestor scrollers. The
  // menuRef guard is required: without it, scrolling the menu's own
  // `overflow-y-auto` list would close it on the first wheel tick.
  useLayoutEffect(() => {
    if (!open) {
      setMenuStyle(null)
      return
    }
    setMenuStyle(computeMenuStyle())
    const onScroll = (e: Event) => {
      if (menuRef.current?.contains(e.target as Node)) return
      close()
    }
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', close)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', close)
    }
  }, [open, computeMenuStyle, close])

  // Reconcile the typed text into a concrete selection when focus leaves.
  const reconcile = useCallback(() => {
    if (exactMatch) {
      onChange({ mode: 'existing', id: exactMatch.id, name: exactMatch.name })
      setQuery(exactMatch.name)
    } else if (showCreate) {
      onChange({ mode: 'new', name: q })
    } else if (!q && allowClear) {
      onChange(NO_LIST)
    } else {
      setQuery(selectionName(value))
    }
  }, [exactMatch, showCreate, q, allowClear, onChange, value])

  // Close on outside click (mousedown), reconciling the current text.
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (wrapRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      reconcile()
      close()
    }
    window.addEventListener('mousedown', handler)
    return () => window.removeEventListener('mousedown', handler)
  }, [open, reconcile, close])

  const pickExisting = (l: ContactListOption) => {
    onChange({ mode: 'existing', id: l.id, name: l.name })
    setQuery(l.name)
    close()
  }
  const pickNew = () => {
    onChange({ mode: 'new', name: q })
    close()
  }
  const selectRow = (i: number) => {
    if (i < filtered.length) pickExisting(filtered[i])
    else if (showCreate) pickNew()
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      reconcile()
      close()
      return
    }
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
      setOpen(true)
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(rowCount - 1, i + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(0, i - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (rowCount > 0) selectRow(Math.min(activeIndex, rowCount - 1))
    }
  }

  const isExistingSelected = value.mode === 'existing'

  return (
    <div ref={wrapRef} className={cn('relative', className)}>
      <div
        className={cn(
          'group flex items-center gap-2 h-9 w-full rounded-md px-2.5 transition-colors duration-150',
          'bg-background border text-foreground',
          'dark:bg-white/[0.02] dark:text-[#f7f8f8]',
          emphasized
            ? 'border-[#5e6ad2]/40 dark:border-[#5e6ad2]/40 ring-1 ring-[#5e6ad2]/15'
            : 'border-border/60 dark:border-white/[0.08] hover:border-border/80 hover:bg-background dark:hover:border-white/[0.14] dark:hover:bg-white/[0.03]',
          // §13: focus is border-only, never a ring, and always dual-mode. This
          // wrapper is the visible control but the focusable node is the nested
          // <input>, so the variant is `focus-within`, not `focus-visible` —
          // otherwise the control shows nothing on keyboard focus. Same token
          // pair as Select.tsx's trigger so the two read identically.
          'focus-within:border-[#7170ff]/55 dark:focus-within:border-[#7170ff]/75',
          open && 'border-[#7170ff]/55 dark:border-[#7170ff]/75',
          disabled && 'opacity-60 cursor-not-allowed',
        )}
      >
        <MagnifyingGlass className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground dark:text-[#8a8f98]" weight="bold" />
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={open && rowCount > 0 ? optionId(Math.min(activeIndex, rowCount - 1)) : undefined}
          aria-label={ariaLabel}
          disabled={disabled}
          autoFocus={autoFocus}
          value={query}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value)
            setActiveIndex(0)
            if (!open) setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          className="flex-1 min-w-0 bg-transparent outline-none placeholder:text-muted-foreground/60 dark:placeholder:text-[#62666d]"
          style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
        />
        {allowClear && query && (
          <button
            type="button"
            aria-label="Clear list"
            onMouseDown={(e) => {
              e.preventDefault()
              setQuery('')
              onChange(NO_LIST)
              setOpen(false)
            }}
            className="flex-shrink-0 text-muted-foreground/70 hover:text-foreground dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] transition-colors"
          >
            <X className="h-3.5 w-3.5" weight="bold" />
          </button>
        )}
        <button
          type="button"
          tabIndex={-1}
          aria-label={open ? 'Hide lists' : 'Show lists'}
          disabled={disabled}
          className="flex-shrink-0 flex items-center justify-center disabled:cursor-not-allowed"
          onMouseDown={(e) => {
            e.preventDefault()
            if (disabled) return
            setOpen((o) => !o)
            inputRef.current?.focus()
          }}
        >
          <CaretDown
            className={cn(
              'h-3.5 w-3.5 text-muted-foreground dark:text-[#8a8f98] transition-transform duration-150',
              open && 'rotate-180',
            )}
            weight="bold"
          />
        </button>
      </div>

      {createPortal(
        <AnimatePresence>
          {open && menuStyle && (
            <motion.div
              initial={{ opacity: 0, y: menuStyle.bottom != null ? 4 : -4, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: menuStyle.bottom != null ? 4 : -4, scale: 0.98 }}
              transition={{ duration: 0.15, ease: [0.16, 1, 0.3, 1] }}
              style={menuStyle}
              className={menuStyle.bottom != null ? 'origin-bottom' : 'origin-top'}
            >
              <ul
                ref={menuRef}
                id={listboxId}
                role="listbox"
                tabIndex={-1}
                style={{ maxHeight: menuStyle.maxHeight }}
                className={cn(
                  'overflow-y-auto rounded-lg p-1',
                  'bg-card border border-border/60 shadow-lg',
                  'dark:bg-[#191a1b] dark:border-white/[0.08]',
                  'dark:shadow-[0_12px_32px_rgba(0,0,0,0.55),0_2px_6px_rgba(0,0,0,0.35),inset_0_0_0_1px_rgba(255,255,255,0.02)]',
                )}
              >
                {filtered.map((l, i) => {
                  const isSelected = isExistingSelected && value.mode === 'existing' && value.id === l.id
                  const isActive = activeIndex === i
                  return (
                    // One node, one role (§57): the `<li role="option">` IS the
                    // interactive element — no nested focusable <button>. Options
                    // are deliberately not focusable; keyboard nav lives on the
                    // input's key handler and is surfaced via `aria-activedescendant`.
                    <li
                      key={l.id}
                      id={optionId(i)}
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => pickExisting(l)}
                      onMouseEnter={() => setActiveIndex(i)}
                      className={cn(
                        'flex w-full cursor-pointer items-center justify-between gap-2 px-2.5 py-1.5 rounded transition-colors duration-100 text-foreground dark:text-[#f7f8f8]',
                        isActive ? 'bg-accent/60 dark:bg-white/[0.06]' : 'hover:bg-accent/40 dark:hover:bg-white/[0.04]',
                        isSelected && 'text-[#5e6ad2] dark:text-[#a5b4fc]',
                      )}
                      style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                    >
                      <span className="truncate text-left">{l.name}</span>
                      {isSelected && <Check className="h-3.5 w-3.5 flex-shrink-0" weight="bold" />}
                    </li>
                  )
                })}

                {filtered.length === 0 && !showCreate && (
                  <li
                    role="presentation"
                    className="px-2.5 py-2 text-muted-foreground/70 dark:text-[#62666d] select-none"
                    style={{ fontSize: '12px' }}
                  >
                    No lists found
                  </li>
                )}

                {showCreate && (
                  <li
                    id={optionId(filtered.length)}
                    role="option"
                    aria-selected={activeIndex === filtered.length}
                    onClick={pickNew}
                    onMouseEnter={() => setActiveIndex(filtered.length)}
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-2 px-2.5 py-1.5 rounded transition-colors duration-100',
                      'text-[#5e6ad2] dark:text-[#a5b4fc]',
                      activeIndex === filtered.length ? 'bg-accent/60 dark:bg-white/[0.06]' : 'hover:bg-accent/40 dark:hover:bg-white/[0.04]',
                    )}
                    style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
                  >
                    <Plus className="h-3.5 w-3.5 flex-shrink-0" weight="bold" />
                    <span className="truncate text-left">Create new list "{q}"</span>
                  </li>
                )}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </div>
  )
}

export default ContactListCombobox
